import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, call } from "@/core/api/client";
import { ENDPOINTS } from "@/core/api/endpoints";
import { QUERY_KEYS } from "@/core/api/query-client";
import type { components } from "@/core/api/schema";
import type { BrandRole } from "@/core/brand/brand-context";

export type Member = components["schemas"]["BrandMember"];
export type TeamInvitation = components["schemas"]["BrandInvitation"];

/** The team and its open invitations (Owners and Managers can see it). */
export function useTeam(brandId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.team(brandId),
    queryFn: async () => (await call(api.GET(ENDPOINTS.team, { params: { path: { brandId } } }))).data,
  });
}

/** A team change can change the signed-in person's own role too: refresh their brand list as well. */
function useTeamChanged(brandId: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.team(brandId) }),
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myBrands }),
    ]);
}

/** Invites by email (inviting the same email again renews the invitation). Owners only. */
export function useInvite(brandId: string) {
  const changed = useTeamChanged(brandId);
  return useMutation({
    mutationFn: async (body: { email: string; role: BrandRole }) =>
      (await call(api.POST(ENDPOINTS.teamInvitations, { params: { path: { brandId } }, body }))).data,
    onSuccess: changed,
  });
}

export function useRevokeInvitation(brandId: string) {
  const changed = useTeamChanged(brandId);
  return useMutation({
    mutationFn: (invitationId: string) =>
      call(api.DELETE(ENDPOINTS.teamInvitation, { params: { path: { brandId, invitationId } } })),
    onSuccess: changed,
  });
}

export function useChangeRole(brandId: string, userId: string) {
  const changed = useTeamChanged(brandId);
  return useMutation({
    mutationFn: async (role: BrandRole) =>
      (await call(api.PATCH(ENDPOINTS.teamMember, { params: { path: { brandId, userId } }, body: { role } })))
        .data,
    onSuccess: changed,
  });
}

/** Removes a member (Owners), or the signed-in person themselves (anyone). Never the last Owner. */
export function useRemoveMember(brandId: string, userId: string) {
  const changed = useTeamChanged(brandId);
  return useMutation({
    mutationFn: () => call(api.DELETE(ENDPOINTS.teamMember, { params: { path: { brandId, userId } } })),
    onSuccess: changed,
  });
}
